/**
 * @file 読んだページの並びを、バーに塗る区間と CSS の gradient に直す純関数を置く。
 *
 * @what: 昇順のページの並びを連続する区間にまとめ、ページ数に対する割合で linear-gradient の色の止め方にする。
 * @why: ページの数だけ要素を作らず、1つの要素の背景で塗る (components/ReadPagesStrip.tsx が使う)。
 * @failure: 副作用を持たない。範囲の外のページは飛ばす。
 */

/** 読んだページ (昇順) を、連続する区間 [from, to] の並びにまとめる。 */
export function pageRuns(pages: readonly number[], pageCount: number): { from: number; to: number }[] {
  const out: { from: number; to: number }[] = [];
  for (const p of pages) {
    if (p < 1 || p > pageCount) continue;
    const last = out[out.length - 1];
    if (last && p <= last.to + 1) last.to = Math.max(last.to, p);
    else out.push({ from: p, to: p });
  }
  return out;
}

/** 読んだ区間を linear-gradient の色の止め方にする。 */
export function runsToGradient(runs: readonly { from: number; to: number }[], pageCount: number): string {
  if (runs.length === 0 || pageCount <= 0) return "none";
  const stops: string[] = [];
  let cursor = 0;
  for (const r of runs) {
    const start = ((r.from - 1) / pageCount) * 100;
    const end = (r.to / pageCount) * 100;
    if (start > cursor) stops.push(`transparent ${cursor.toFixed(2)}% ${start.toFixed(2)}%`);
    stops.push(`var(--study-read-fill) ${start.toFixed(2)}% ${end.toFixed(2)}%`);
    cursor = end;
  }
  if (cursor < 100) stops.push(`transparent ${cursor.toFixed(2)}% 100%`);
  return `linear-gradient(to right, ${stops.join(", ")})`;
}
